import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/axios';
import { useAuthCheck } from '../../hooks/useAuth';

const DEBOUNCE_DELAY = 300;
const PAGE_SIZE = 50;

function ContactsPage() {
  // Check authentication before rendering
  useAuthCheck();
  
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [contactInsights, setContactInsights] = useState({});
  
  const debounceTimeoutRef = useRef(null);
  const observerRef = useRef(null);
  const loadingRef = useRef(null);

  // Fetch contacts - uses different APIs based on search state
  const fetchContacts = useCallback(async (isNewSearch = false, searchTerm = searchQuery) => {
    if (loading) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let response;
      
      // If no search term, use latest contacts with insights API
      if (!searchTerm?.trim()) {
        response = await apiClient.get('/timeline/contacts/latest-with-insights', {
          params: { limit: 5 }
        });
        
        if (response.data.success) {
          setContacts(response.data.contacts || []);
          setTotalCount(response.data.contacts?.length || 0);
          setHasMore(false); // No pagination for latest contacts
          setCursor(null);
          
          // Fetch insights asynchronously
          if (response.data.contacts?.length > 0) {
            fetchInsightsForContacts(response.data.contacts);
          }
        }
      } else {
        // Use regular contacts API with search and pagination
        const params = {
          limit: PAGE_SIZE,
          search: searchTerm.trim()
        };
        
        // Use cursor for pagination (not for new searches)
        if (!isNewSearch && cursor) {
          params.cursor = cursor;
        }
        
        response = await apiClient.get('/timeline/contacts', { params });
        
        if (response.data.success) {
          const newContacts = response.data.contacts;
          
          if (isNewSearch) {
            setContacts(newContacts);
          } else {
            setContacts(prev => [...prev, ...newContacts]);
          }
          
          setTotalCount(response.data.total_count);
          setHasMore(response.data.has_more);
          setCursor(response.data.next_cursor);
          
          // Fetch insights asynchronously
          if (newContacts?.length > 0) {
            fetchInsightsForContacts(newContacts);
          }
        }
      }
      
      if (!response.data.success) {
        setError('Failed to load contacts');
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
      if (initialLoading) {
        setInitialLoading(false);
      }
    }
  }, [loading, cursor, searchQuery, initialLoading]);


  // Fetch insights for a list of contacts
  const fetchInsightsForContacts = async (contactsList) => {
    try {
      const insightsPromises = contactsList.map(async (contact) => {
        try {
          const response = await apiClient.get(`/analyze/aggregation/contacts/${contact.id}/insights`, {
            params: { limit: 20 }
          });
          return {
            contactId: contact.id,
            insights: response.data || []
          };
        } catch (err) {
          console.error(`Error fetching insights for contact ${contact.id}:`, err);
          return {
            contactId: contact.id,
            insights: []
          };
        }
      });
      
      const results = await Promise.all(insightsPromises);
      
      const newInsights = {};
      
      results.forEach(({ contactId, insights }) => {
        newInsights[contactId] = insights;
      });
      
      setContactInsights(prev => ({ ...prev, ...newInsights }));
    } catch (err) {
      console.error('Error fetching insights:', err);
    }
  };

  // Extract insight types with counters and severity from insights
  const extractInsightTags = (insights) => {
    const tagMap = new Map();
    
    insights.forEach(insight => {
      const type = insight.insight_type;
      // Each stored insight already represents one trigger event (grouped by
      // conflict key), so count insights rather than summing total_issues,
      // which is the number of conflicting events behind each insight.
      const severity = insight.severity || 'Medium';

      if (tagMap.has(type)) {
        const existing = tagMap.get(type);
        tagMap.set(type, {
          ...existing,
          count: existing.count + 1,
          // Use highest severity
          severity: getHigherSeverity(existing.severity, severity)
        });
      } else {
        tagMap.set(type, {
          type,
          count: 1,
          severity,
          displayName: formatInsightType(type)
        });
      }
    });
    
    return Array.from(tagMap.values()).sort((a, b) => {
      // Sort by severity first, then by count
      const severityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
      const severityDiff = (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0);
      if (severityDiff !== 0) return severityDiff;
      return b.count - a.count;
    });
  };

  // Get higher severity between two severities
  const getHigherSeverity = (severity1, severity2) => {
    const severityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    const level1 = severityOrder[severity1?.toLowerCase()] || 0;
    const level2 = severityOrder[severity2?.toLowerCase()] || 0;
    return level1 >= level2 ? severity1 : severity2;
  };

  // Format insight type for display
  const formatInsightType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Debounced search
  const debouncedSearch = useCallback((searchTerm) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      setCursor(null); // Reset cursor for new search
      setHasMore(true);
      fetchContacts(true, searchTerm);
    }, DEBOUNCE_DELAY);
  }, [fetchContacts]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Handle contact click - navigate to timeline
  const handleContactClick = (contact) => {
    navigate(`/timeline-list?contact=${encodeURIComponent(contact.id)}`);
  };


  // Handle insight tag click - just navigate to timeline
  const handleInsightTagClick = (insightType, e) => {
    e.stopPropagation(); // Prevent contact click
    // No filtering, just prevent event bubbling
  };


  // Infinite scroll observer
  useEffect(() => {
    if (!loadingRef.current || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchContacts(false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadingRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, fetchContacts]);

  // Initial load
  useEffect(() => {
    // Fetch contacts (will use latest contacts API since no search term)
    fetchContacts(true, '');
    
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get insight tag styles based on severity
  const getInsightTagStyles = (severity) => {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      color: 'white',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none',
      outline: 'none'
    };

    switch (severity?.toLowerCase()) {
      case 'critical':
        return {
          ...baseStyle,
          backgroundColor: '#ff4d4f',
          boxShadow: '0 2px 4px rgba(255, 77, 79, 0.3)'
        };
      case 'high':
        return {
          ...baseStyle,
          backgroundColor: '#ff7a45',
          boxShadow: '0 2px 4px rgba(255, 122, 69, 0.3)'
        };
      case 'medium':
        return {
          ...baseStyle,
          backgroundColor: '#ffa940',
          boxShadow: '0 2px 4px rgba(255, 169, 64, 0.3)'
        };
      case 'low':
        return {
          ...baseStyle,
          backgroundColor: '#52c41a',
          boxShadow: '0 2px 4px rgba(82, 196, 26, 0.3)'
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: '#1890ff',
          boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)'
        };
    }
  };

  // Styles
  const pageStyle = {
    marginLeft: window.innerWidth <= 768 ? '0' : '20px',
    padding: '2rem',
    minHeight: '100vh',
    backgroundColor: 'var(--bg-primary)',
    paddingTop: window.innerWidth <= 768 ? '80px' : '2rem'
  };

  const headerStyle = {
    marginBottom: '2rem'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: '0 0 0.5rem 0'
  };

  const subtitleStyle = {
    color: 'var(--text-muted)',
    fontSize: '1rem',
    margin: '0 0 1.5rem 0'
  };

  const searchContainerStyle = {
    marginBottom: '1.5rem'
  };

  const searchInputStyle = {
    width: '100%',
    maxWidth: '400px',
    padding: '0.75rem',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    transition: 'var(--transition-standard)'
  };


  const loadingStyle = {
    textAlign: 'center',
    padding: '2rem',
    color: 'var(--text-muted)'
  };

  const errorStyle = {
    textAlign: 'center',
    padding: '2rem',
    color: 'var(--color-error)',
    backgroundColor: 'var(--bg-error)',
    borderRadius: '8px',
    border: '1px solid var(--border-error)'
  };

  const statsStyle = {
    display: 'flex',
    gap: '2rem',
    marginBottom: '1rem',
    fontSize: '0.875rem',
    color: 'var(--text-muted)'
  };

  if (initialLoading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>
          <div>Loading contacts...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>Contacts</h1>
        <p style={subtitleStyle}>
          Browse and search through all contacts with their insights
        </p>
        
        <div style={statsStyle}>
          {searchQuery.trim() ? (
            <>
              <span>Total: {totalCount} contacts</span>
              <span>Showing: {contacts.length} contacts</span>
            </>
          ) : (
            <span>Latest contacts with insights</span>
          )}
        </div>
      </div>

      <div style={searchContainerStyle}>
        <input
          type="text"
          placeholder="Search contacts by name or email..."
          value={searchQuery}
          onChange={handleSearchChange}
          style={searchInputStyle}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--color-primary)';
            e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--border-color)';
            e.target.style.boxShadow = 'none';
          }}
        />
      </div>


      {error && (
        <div style={errorStyle}>
          {error}
        </div>
      )}

      {/* Single Contact List */}
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '20px'
      }}>
        {contacts.map((contact) => {
          const insights = contactInsights[contact.id] || [];
          
          return (
            <div
              key={contact.id}
              onClick={() => handleContactClick(contact)}
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}>
                  {contact.first_name ? contact.first_name[0].toUpperCase() : 
                   contact.email ? contact.email[0].toUpperCase() : '?'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: '600', 
                    fontSize: '16px', 
                    color: '#111827',
                    marginBottom: '2px'
                  }}>
                    {contact.first_name && contact.last_name 
                      ? `${contact.first_name} ${contact.last_name}`
                      : contact.email}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#6b7280'
                  }}>
                    {contact.email}
                  </div>
                </div>
              </div>
              
              {/* Show insight tags with counters */}
              {(() => {
               
                const insightTags = extractInsightTags(insights);
                const totalInsights = insightTags.length;
                
                return (
                  <>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      <strong>{totalInsights}</strong> insight{totalInsights !== 1 ? 's' : ''}
                    </div>
                    
                    {insightTags.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        marginTop: '8px'
                      }}>
                        {insightTags.map((tag, index) => (
                          <div
                            key={`${tag.type}-${index}`}
                            style={getInsightTagStyles(tag.severity)}
                            onClick={(e) => handleInsightTagClick(tag.type, e)}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = e.target.style.boxShadow.replace('0 2px 4px', '0 4px 8px');
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = e.target.style.boxShadow.replace('0 4px 8px', '0 2px 4px');
                            }}
                          >
                            <span>{tag.displayName}</span>
                            <span style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                              borderRadius: '8px',
                              padding: '1px 4px',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}>
                              {tag.count}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
              
              {/* Show last analyzed timestamp if available */}
              {contact.latest_analysis_timestamp && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#9ca3af',
                  marginTop: '8px'
                }}>
                  Last analyzed: {new Date(contact.latest_analysis_timestamp).toLocaleDateString()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Infinite scroll trigger - only show when searching */}
      {searchQuery.trim() && hasMore && !loading && (
        <div ref={loadingRef} style={{ height: '20px' }} />
      )}

      {loading && !initialLoading && (
        <div style={loadingStyle}>
          <div>Loading more contacts...</div>
        </div>
      )}

      {searchQuery.trim() && !hasMore && contacts.length > 0 && (
        <div style={loadingStyle}>
          <div>All contacts loaded</div>
        </div>
      )}

      {!loading && contacts.length === 0 && !error && (
        <div style={loadingStyle}>
          <div>{searchQuery.trim() ? 'No contacts found' : 'No contacts with insights available'}</div>
        </div>
      )}
    </div>
  );
}

export default ContactsPage;
